/**
 * 「参考片在拍什么」——(2026-08-14)备料页蓝图条下面的内容层面板。
 *
 * ═══ 为什么加这块 ═══════════════════════════════════════════════════════════
 * 老板原话:目的地不该只显示拍法/结构,要让用户感知到「我们能准确分析爆款视频内容」。
 * 逐拍摊开(画面 + 那一拍说了什么)比任何抽象的结构描述都有说服力 ——
 * 用户一眼看见我们把这条片拆到了逐拍逐句的粒度。
 *
 * ═══ 它与「想拍什么」框的分工(这条是硬边界)═══════════════════════════════
 * 本面板的文本走 `explosiveReferenceContent` **独立通道**;
 * 「想拍什么」框(= `userInput`)仍然只装用户自己的产品事实。
 * 两者混在一个框里的后果是实测过的:`classify_input` 判 SELLING_POINTS →
 * 引擎被命令「每个卖点绑定一个节拍、不得新增」→ 成片通篇讲参考片的产品
 * (0812「带过去都是节奏」,A/B 28/52 分 vs 拿掉后 65 分)。
 *
 * 所以这里可编辑、可清空,但它的编辑结果永远不会流进 userInput。
 *
 * ★`readOnly`(2026-10-10 追爆原话复用 P4):数字人 / 新闻舱两处这块**根本不上送**,
 *   可编辑却不生效 = 骗用户。这两处改只读、默认折叠;视频/海报仍可编辑(那边真走独立通道)。
 */
import { ChevronDown, Eye, RotateCcw } from "lucide-react";
import { useState } from "react";

import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/textarea";

export function ReferenceContentPanel({
  content,
  original,
  disabled,
  onChange,
  readOnly = false,
}: {
  content: string;
  /** 路由带来的原始版本 —— 用户改乱了能一键还原。 */
  original: string;
  disabled?: boolean;
  onChange?: (next: string) => void;
  /** 只读折叠展示(不上送的出口用,见头注)。 */
  readOnly?: boolean;
}) {
  const [open, setOpen] = useState(!readOnly);
  if (!content && !original) return null;
  const beatCount = content.split("\n").filter((l) => /^\d+(\.\d+)?-\d/.test(l.trim())).length;
  const dirty = content !== original;

  return (
    <div
      data-testid="reference-content-panel"
      className="rounded-lg border-2 border-primary/40 bg-primary/[0.03]"
    >
      <div className="flex items-center gap-2 px-3.5 py-2.5">
        <Eye className="h-4 w-4 shrink-0 text-primary" />
        <button
          type="button"
          data-testid="reference-content-toggle"
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 text-left hover:text-primary"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="text-sm font-semibold">参考片在拍什么</span>
          <span className="text-2xs font-normal text-muted-foreground">
            {beatCount > 0 ? `逐拍 ${beatCount} 拍 · 画面+口播都在` : "已拆出内容"}
          </span>
          <ChevronDown
            className={`ml-auto h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && dirty && !readOnly && onChange && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 shrink-0 gap-1 px-2 text-2xs"
            disabled={disabled}
            onClick={() => onChange(original)}
          >
            <RotateCcw className="h-3 w-3" />
            还原
          </Button>
        )}
      </div>
      {open && (
        <div className="space-y-1.5 border-t border-primary/20 px-3.5 py-2.5">
          {readOnly ? (
            <div
              data-testid="reference-content-text"
              className="max-h-60 overflow-y-auto whitespace-pre-wrap rounded-md border border-border/60 bg-card px-2.5 py-2 font-mono text-2xs leading-relaxed"
            >
              {content}
            </div>
          ) : (
            <Textarea
              data-testid="reference-content-text"
              rows={8}
              value={content}
              disabled={disabled}
              onChange={(e) => onChange?.(e.target.value)}
              className="resize-none bg-card font-mono text-2xs leading-relaxed"
            />
          )}
          <p className="text-2xs leading-relaxed text-muted-foreground">
            {readOnly ? (
              <>这是我们从原片拆出来的内容,只给你对照看,不会交给引擎。</>
            ) : (
              <>
                这是我们从原片拆出来的<b>内容</b>,会作为「照着拍什么」交给引擎。
                你自己的产品写在下面的「想拍什么」框里 ——
                两个框分开走,成片讲的是你的产品,不是参考片的。
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
